// The API's structured error shape (api-contract.md §0.9), as a throwable.
//
// `message` is human text the API guarantees is safe to display and PHI-free,
// so screens surface it verbatim. That is how the three broker refusals reach
// the user now that a rejected decision writes no row: `Blocked by an active
// hold`, `Out of order — record status is <status>`, `Not authorized for this
// step` arrive as 409/409/403 messages.

export interface ApiErrorBody {
  code: string
  message: string
  details?: Record<string, unknown>
  request_id?: string
}

export class ApiError extends Error {
  readonly status: number
  readonly code: string
  readonly details?: Record<string, unknown>
  readonly requestId?: string

  constructor(status: number, body: ApiErrorBody) {
    super(body.message || body.code || `HTTP ${status}`)
    this.name = 'ApiError'
    this.status = status
    this.code = body.code
    this.details = body.details
    this.requestId = body.request_id
  }

  /** A state conflict the user can act on (hold, out of order, version). */
  get isConflict(): boolean {
    return this.status === 409
  }

  get isForbidden(): boolean {
    return this.status === 403
  }

  get isNotFound(): boolean {
    return this.status === 404
  }
}

export function isApiError(e: unknown): e is ApiError {
  return e instanceof ApiError
}

/** The message to show a user for any thrown value. */
export function errorMessage(e: unknown): string {
  if (isApiError(e)) return e.message
  if (e instanceof Error) return e.message
  return String(e)
}
